import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { commitApi } from '@/api/commitApi'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { morphToast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import { useModalStore } from '@/hooks/useModalStore'
import { useCommitsStore } from '@/stores/commitsStore'
import { useDraftStore } from '@/stores/draftStore'
import { useConflictStore } from '@/stores/conflictStore'
import { ApiError } from '@/api/httpClient'
import { arrayToKeyed } from '@/services/metadata/keyed'
import { diffToOps } from '@/stores/draftStore'

export function CreateCommitModal() {
  const { t } = useTranslation()
  const open = useModalStore((s) => s.createCommitOpen)
  const close = useModalStore((s) => s.closeCreateCommit)
  const createCommit = useCommitsStore((s) => s.createCommit)
  const currentProjectId = useDraftStore((s) => s.currentProjectId)
  const baseCommitId = useDraftStore((s) => s.baseCommitId)
  const draft = useDraftStore((s) => s.draft)
  const draftMetadata = useDraftStore((s) => s.metadata)
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [changeCount, setChangeCount] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setDescription('')
  }, [open])

  useEffect(() => {
    if (!open || !currentProjectId || !draftMetadata) {
      setChangeCount(null)
      return
    }

    let cancelled = false

    async function loadSummary() {
      if (!baseCommitId) {
        if (!cancelled) setChangeCount(Object.keys(draftMetadata.tables).length)
        return
      }

      try {
        const head = await commitApi.get(currentProjectId, baseCommitId)
        const ops = diffToOps(arrayToKeyed(head.metadata as never), draftMetadata)
        if (!cancelled) setChangeCount(ops.length)
      } catch {
        if (!cancelled) setChangeCount(null)
      }
    }

    void loadSummary()
    return () => {
      cancelled = true
    }
  }, [open, currentProjectId, baseCommitId, draftMetadata])

  const lastModifiedById = typeof draft?.last_modified_by === 'object'
    ? draft.last_modified_by?.id ?? null
    : draft?.last_modified_by ?? null
  const hasCollaboratorChanges = lastModifiedById != null && currentUserId != null && lastModifiedById !== currentUserId

  async function handleCreate() {
    if (!currentProjectId) return
    const trimmedName = name.trim() || defaultName()

    setSubmitting(true)
    try {
      const commit = await createCommit(currentProjectId, {
        name: trimmedName,
        description: description.trim(),
        baseCommitId,
      })
      close()
      morphToast.success(t('commits.created', `Versão "${commit.name}" criada!`))
    } catch (err) {
      if (err instanceof ApiError && (err.raw as Record<string, unknown>)?.code === 'PROJECT_COMMIT_CONFLICT') {
        if (!currentProjectId) return
        const conflictData = err.raw as { current_commit_id?: number }
        // Update projectsStore with the new HEAD before starting conflict flow
        if (conflictData.current_commit_id) {
          const { useProjectsStore } = await import('@/stores/projectsStore')
          useProjectsStore.setState((s) => ({
            projects: s.projects.map((p) =>
              p.id === currentProjectId
                ? { ...p, current_commit_id: conflictData.current_commit_id! }
                : p,
            ),
          }))
        }
        close()
        useConflictStore.getState().begin({
          projectId: currentProjectId,
          commitName: trimmedName,
          commitDescription: description.trim(),
          headCommitId: conflictData.current_commit_id,
        })
      } else {
        morphToast.error(err instanceof Error ? err.message : t('common.error', 'Erro ao criar versão'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={t('commits.createTitle', 'Criar nova versão')}
      onClose={close}
      footer={(
        <>
          <Button variant="ghost" onClick={close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={submitting || !currentProjectId}>
            {submitting
              ? t('common.loading', 'Aguarde...')
              : t('commits.createAction', 'Salvar versão')}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div
          className="rounded-lg border px-3 py-3 text-[12px]"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
        >
          <div className="font-medium">
            {changeCount == null
              ? t('commits.collectiveSummaryFallback', 'Este commit publica o rascunho compartilhado atual.')
              : t('commits.collectiveSummary', `${changeCount} alterações sobre o último commit.`)}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {hasCollaboratorChanges
              ? t('commits.collectiveCollaborative', 'Inclui alterações suas e de outros colaboradores.')
              : t('commits.collectiveSolo', 'Inclui o estado atual do trabalho em andamento compartilhado.')}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('commits.nameLabel', 'Nome da versão')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName()}
            className="morph-input"
            disabled={submitting}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('commits.descriptionLabel', 'Descrição (opcional)')}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('commits.descriptionPlaceholder', 'O que mudou nesta versão?')}
            className="morph-textarea min-h-[90px]"
            disabled={submitting}
          />
        </div>
      </div>
    </Dialog>
  )
}

function defaultName(): string {
  return new Date().toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}
